/**
 * ORBAT service for API communication with faction, unit group, and unit endpoints.
 */

import apiClient from '../api/client';
import {
    Faction,
    FactionCreate,
    FactionUpdate,
    FactionListResponse,
    UnitGroup,
    UnitGroupCreate,
    UnitGroupUpdate,
    UnitGroupListResponse,
    UnitGroupTreeResponse,
    Unit,
    UnitCreate,
    UnitUpdate,
    UnitPositionUpdate,
    UnitListResponse,
    AffiliationTypeListResponse,
} from '../types/orbat';
import { MobileCredential } from '../types/mobileCredentials';

// =============================================================================
// Affiliation API
// =============================================================================

/**
 * Get all available affiliation types.
 */
export async function getAffiliations(): Promise<AffiliationTypeListResponse> {
    const response = await apiClient.get<AffiliationTypeListResponse>(
        '/api/v1/affiliations'
    );
    return response.data;
}

// =============================================================================
// Faction API
// =============================================================================

/**
 * Get all factions for an event.
 */
export async function getFactions(eventId: string): Promise<FactionListResponse> {
    const response = await apiClient.get<FactionListResponse>(
        `/api/v1/events/${eventId}/factions`
    );
    return response.data;
}

/**
 * Get a specific faction by ID.
 */
export async function getFaction(eventId: string, factionId: string): Promise<Faction> {
    const response = await apiClient.get<Faction>(
        `/api/v1/events/${eventId}/factions/${factionId}`
    );
    return response.data;
}

/**
 * Create a new faction.
 */
export async function createFaction(
    eventId: string,
    data: FactionCreate
): Promise<Faction> {
    const response = await apiClient.post<Faction>(
        `/api/v1/events/${eventId}/factions`,
        data
    );
    return response.data;
}

/**
 * Update an existing faction.
 */
export async function updateFaction(
    eventId: string,
    factionId: string,
    data: FactionUpdate
): Promise<Faction> {
    const response = await apiClient.patch<Faction>(
        `/api/v1/events/${eventId}/factions/${factionId}`,
        data
    );
    return response.data;
}

/**
 * Delete a faction.
 */
export async function deleteFaction(eventId: string, factionId: string): Promise<void> {
    await apiClient.delete(`/api/v1/events/${eventId}/factions/${factionId}`);
}

// =============================================================================
// Unit Group API
// =============================================================================

/**
 * Get all unit groups for an event, optionally filtered by faction.
 */
export async function getUnitGroups(
    eventId: string,
    factionId?: string
): Promise<UnitGroupListResponse> {
    const params = factionId ? { faction_id: factionId } : {};
    const response = await apiClient.get<UnitGroupListResponse>(
        `/api/v1/events/${eventId}/unit-groups`,
        { params }
    );
    return response.data;
}

/**
 * Get a unit group with its full unit tree.
 */
export async function getUnitGroupTree(
    eventId: string,
    unitGroupId: string
): Promise<UnitGroupTreeResponse> {
    const response = await apiClient.get<UnitGroupTreeResponse>(
        `/api/v1/events/${eventId}/unit-groups/${unitGroupId}/tree`
    );
    return response.data;
}

/**
 * Create a new unit group.
 */
export async function createUnitGroup(
    eventId: string,
    data: UnitGroupCreate
): Promise<UnitGroup> {
    const response = await apiClient.post<UnitGroup>(
        `/api/v1/events/${eventId}/unit-groups`,
        data
    );
    return response.data;
}

/**
 * Update an existing unit group.
 */
export async function updateUnitGroup(
    eventId: string,
    unitGroupId: string,
    data: UnitGroupUpdate
): Promise<UnitGroup> {
    const response = await apiClient.patch<UnitGroup>(
        `/api/v1/events/${eventId}/unit-groups/${unitGroupId}`,
        data
    );
    return response.data;
}

/**
 * Delete a unit group.
 */
export async function deleteUnitGroup(
    eventId: string,
    unitGroupId: string
): Promise<void> {
    await apiClient.delete(`/api/v1/events/${eventId}/unit-groups/${unitGroupId}`);
}

// =============================================================================
// Unit API
// =============================================================================

/**
 * Get all units for an event.
 */
export async function getUnits(eventId: string): Promise<UnitListResponse> {
    const response = await apiClient.get<UnitListResponse>(
        `/api/v1/events/${eventId}/units`
    );
    return response.data;
}

/**
 * Create a new unit.
 */
export async function createUnit(eventId: string, data: UnitCreate): Promise<Unit> {
    const response = await apiClient.post<Unit>(
        `/api/v1/events/${eventId}/units`,
        data
    );
    return response.data;
}

/**
 * Update an existing unit.
 */
export async function updateUnit(
    eventId: string,
    unitId: string,
    data: UnitUpdate
): Promise<Unit> {
    const response = await apiClient.patch<Unit>(
        `/api/v1/events/${eventId}/units/${unitId}`,
        data
    );
    return response.data;
}

/**
 * Update a unit's position in the hierarchy (for drag-and-drop).
 */
export async function updateUnitPosition(
    eventId: string,
    unitId: string,
    data: UnitPositionUpdate
): Promise<Unit> {
    const response = await apiClient.patch<Unit>(
        `/api/v1/events/${eventId}/units/${unitId}/position`,
        data
    );
    return response.data;
}

/**
 * Reorder a group of sibling units. `orderedUnitIds` must list every child of
 * a single parent in the desired order; the backend renumbers them 0..N-1.
 */
export async function reorderUnits(
    eventId: string,
    orderedUnitIds: string[]
): Promise<UnitListResponse> {
    const response = await apiClient.patch<UnitListResponse>(
        `/api/v1/events/${eventId}/units/reorder`,
        { ordered_unit_ids: orderedUnitIds }
    );
    return response.data;
}

/**
 * Delete a unit.
 */
export async function deleteUnit(eventId: string, unitId: string): Promise<void> {
    await apiClient.delete(`/api/v1/events/${eventId}/units/${unitId}`);
}

/**
 * Provision mobile credentials for a unit.
 * Returns client_id and client_secret for mobile app authentication.
 */
export async function provisionMobileCredentials(
    eventId: string,
    unitId: string
): Promise<MobileCredential> {
    const response = await apiClient.post<MobileCredential>(
        `/api/v1/events/${eventId}/units/${unitId}/mobile-credentials`
    );
    return response.data;
}

// Export all functions as a service object for convenience
export const orbatService = {
    // Affiliations
    getAffiliations,
    // Factions
    getFactions,
    getFaction,
    createFaction,
    updateFaction,
    deleteFaction,
    // Unit Groups
    getUnitGroups,
    getUnitGroupTree,
    createUnitGroup,
    updateUnitGroup,
    deleteUnitGroup,
    // Units
    getUnits,
    createUnit,
    updateUnit,
    updateUnitPosition,
    reorderUnits,
    deleteUnit,
    provisionMobileCredentials,
};

export default orbatService;
